import { useEffect, useState } from 'react';

export type ImageBrightness = 'light' | 'dark';

const CANVAS_MAX_SIDE = 100;
const BRIGHTNESS_THRESHOLD = 130;
const ALPHA_THRESHOLD = 16;

const computeAverageBrightness = (
  imageData: Uint8ClampedArray
): number | null => {
  let sum = 0;
  let count = 0;
  for (let i = 0; i < imageData.length; i += 4) {
    if (imageData[i + 3] < ALPHA_THRESHOLD) continue;
    sum +=
      0.299 * imageData[i] +
      0.587 * imageData[i + 1] +
      0.114 * imageData[i + 2];
    count += 1;
  }
  return count > 0 ? sum / count : null;
};

export const useImageBrightness = (
  imageUrl: string | undefined | null,
  fallback: ImageBrightness = 'dark'
): ImageBrightness => {
  const [brightness, setBrightness] = useState<ImageBrightness>(fallback);

  useEffect(() => {
    if (!imageUrl) {
      setBrightness(fallback);
      return;
    }

    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      if (cancelled) return;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const scale = Math.min(
        CANVAS_MAX_SIDE / img.width,
        CANVAS_MAX_SIDE / img.height,
        1
      );
      canvas.width = Math.max(1, Math.floor(img.width * scale));
      canvas.height = Math.max(1, Math.floor(img.height * scale));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      try {
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const avg = computeAverageBrightness(data);
        if (avg === null || cancelled) return;
        const next: ImageBrightness =
          avg > BRIGHTNESS_THRESHOLD ? 'light' : 'dark';
        setBrightness(next);
      } catch (err) {
        console.warn(
          '[useImageBrightness] canvas read failed (likely CORS) — falling back',
          err
        );
        if (!cancelled) setBrightness(fallback);
      }
    };

    img.onerror = (err) => {
      console.warn('[useImageBrightness] image load failed', err);
      if (!cancelled) setBrightness(fallback);
    };

    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl, fallback]);

  return brightness;
};
